import { Box, InputBase, InputLabel } from '@mui/material';

/**
 * The app's text input: label above, a 32px pill below, same height and corner
 * as the filter bar's fields.
 *
 * TWO WAYS TO DRIVE IT, because the app has two kinds of form:
 *
 *   formik  — `formik={formik} name="firstName"`, the original contract. The
 *             user modals are built this way and are untouched.
 *   controlled — `value` + `onChange`, for the dialogs that hold their state in
 *             useState. Passing `formik` used to be mandatory: the component
 *             read `formik.values[name]` unguarded, so a plain controlled use
 *             threw on the first render. That is why the Pulse dialogs had
 *             grown their own MUI TextFields instead of using this.
 *
 * `multiline` relaxes the pill to a rounded rectangle and lets it grow — a
 * 32px capsule cannot hold three lines of commentary.
 */
function InputBox({
  label,
  disabled = false,
  display,
  name = '',
  formik,
  required = false,
  type = 'text',
  inputProps = '',
  width = '210px',
  // Controlled mode.
  value,
  onChange,
  error,
  placeholder,
  startAdornment,
  // Rendered as-is at the right of the pill — e.g. a show/hide toggle
  // (PasswordInputBox). Unlike startAdornment it is not wrapped, because it is
  // usually a control rather than a unit label.
  endAdornment,
  autoComplete,
  id,
  multiline = false,
  minRows = 3,
  maxRows = 8,
}) {
  const controlled = formik === undefined;
  const currentValue = controlled ? (value ?? '') : formik.values[name];
  const handleChange = controlled ? onChange : formik.handleChange;
  const shownError = controlled
    ? error
    : formik.touched[name] && formik.errors[name]
      ? formik.errors[name]
      : null;

  // The generated "Enter <label>" hint only makes sense when there IS a label;
  // a controlled caller can pass its own placeholder for the unlabelled case.
  const hint =
    placeholder ??
    (label ? (label.length > 13 ? `Enter ${label.slice(0, 13)}...` : `Enter ${label}`) : '');

  return (
    <Box sx={{ ...{ display: 'flex', flexDirection: 'column' }, display: display || 'auto' }}>
      {label && (
        <InputLabel htmlFor={id}>
          <span
            style={{ color: '#404040', fontSize: '13px', fontWeight: '400', marginBottom: '4px' }}
          >
            {label}
          </span>
          {required && <span style={{ color: '#FD5707' }}>*</span>}
        </InputLabel>
      )}
      <InputBase
        type={type}
        startAdornment={
          startAdornment ? (
            <Box component="span" sx={{ color: '#6B7280', fontSize: 13, mr: 0.5 }}>
              {startAdornment}
            </Box>
          ) : null
        }
        endAdornment={endAdornment ?? null}
        autoComplete={autoComplete}
        id={id}
        multiline={multiline}
        minRows={multiline ? minRows : undefined}
        maxRows={multiline ? maxRows : undefined}
        sx={{
          borderRadius: multiline ? '12px' : '20px',
          height: multiline ? 'auto' : '32px',
          py: multiline ? 0.85 : 0,
          border: '1px solid #E4E4E4',
          pl: 1,
          pr: endAdornment ? 0.5 : 0,
          mb: 0.5,
          width,
          fontSize: 13,
          // The number spinner is browser chrome the app never uses.
          '& input[type=number]': { MozAppearance: 'textfield' },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
        disabled={disabled}
        value={currentValue}
        onChange={handleChange}
        inputProps={inputProps}
        placeholder={hint}
        name={name}
      />
      {shownError ? <p style={{ color: 'red', fontSize: '12px' }}>{shownError}</p> : null}
    </Box>
  );
}

export default InputBox;
